import { useToast } from '@components/index'
import { apiClient } from '@services/api-client'
import { SNA_PREFIX } from '@services/utils'
import { useOnError } from '@services/utils/onError' // Added import
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { ChatDetail } from 'types/api'
import type { BaseParams } from 'types/utils'

type MessageInput =
  | {
      message: string
      attachments: File[] | undefined
    }
  | {
      attachments: File[]
      message: string | undefined
    }

export function useSendMessage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const onError = useOnError() // Added useOnError

  const { t } = useTranslation('chat') // Added translation hook

  async function sendMessage({
    id,
    body
  }: BaseParams<[MessageInput, ChatDetail], 'POST'>) {
    const formData = new FormData()

    if (body?.attachments && body.attachments.length > 0) {
      body.attachments.forEach((file) => {
        formData.append('attachments', file) // No need for indexed naming
      })
    }

    if (body?.message) formData.append('message', body.message)

    const res = await apiClient.post<ChatDetail>(
      `${SNA_PREFIX}chats/${id}/send-message/`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    )

    return res.data
  }

  return useMutation({
    mutationFn: sendMessage,
    onSuccess: (data, variables) => {
      variables?.params?.onSuccess?.(data)
      toast.success({ message: t('toast.messageSent') })
      queryClient.invalidateQueries({ queryKey: ['chat'] })
    },
    onError // Updated to use useOnError
  })
}
